import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import DisableConfirm from "@/components/DisableConfirm";
import { formatMoney } from "@/lib/currency";
import { toast } from "sonner";
import { Eye, Pencil, Power, Plus, RotateCcw, CalendarClock, AlertTriangle } from "lucide-react";

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return s; }
}
function ymFromMonths(m) {
  const y = Math.floor((m || 0) / 12);
  const mo = (m || 0) % 12;
  return `${y}Y ${mo}M`;
}

export default function SubscriptionPlansList() {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [pubs, setPubs] = useState([]);
  const [pers, setPers] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [pubId, setPubId] = useState("");
  const [perId, setPerId] = useState("");
  const [items, setItems] = useState(null);
  const [view, setView] = useState(null);
  const [disableTarget, setDisableTarget] = useState(null);
  const [disabling, setDisabling] = useState(false);
  const [renewTarget, setRenewTarget] = useState(null);
  const [renewStart, setRenewStart] = useState("");
  const [renewEnd, setRenewEnd] = useState("");

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/organizations/active-list"); setOrgs(data); } catch { /* */ }
    })();
  }, []);

  useEffect(() => {
    setPubId(""); setPerId(""); setPubs([]); setPers([]); setItems(null);
    if (!orgId) return;
    (async () => {
      try { const { data } = await api.get("/publication-houses/active-list", { params: { organization_id: orgId } }); setPubs(data); } catch { /* */ }
    })();
  }, [orgId]);

  useEffect(() => {
    setPerId(""); setPers([]); setItems(null);
    if (!pubId) return;
    (async () => {
      try {
        const { data } = await api.get("/periodicals", { params: { publication_house_id: pubId, page: 1, page_size: 100 } });
        setPers(data.items.filter((x) => x.status === "active"));
      } catch { /* */ }
    })();
  }, [pubId]);

  useEffect(() => {
    if (!perId) { setItems(null); return; }
    load();
    // eslint-disable-next-line
  }, [perId]);

  const load = async () => {
    setItems(null);
    try {
      const { data } = await api.get("/subscription-plans", { params: { periodical_id: perId } });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setItems([]);
    }
  };

  const handleDisable = async () => {
    setDisabling(true);
    try {
      await api.patch(`/subscription-plans/${disableTarget.id}/disable`);
      toast.success("Plan disabled");
      setDisableTarget(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setDisabling(false); }
  };

  const handleActivate = async (plan) => {
    const expired = new Date(plan.end_date) < new Date(new Date().toDateString());
    if (expired) {
      const today = new Date().toISOString().slice(0, 10);
      setRenewTarget(plan);
      setRenewStart(today);
      setRenewEnd("");
      return;
    }
    try {
      await api.patch(`/subscription-plans/${plan.id}/activate`);
      toast.success("Plan activated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const submitRenew = async () => {
    try {
      await api.patch(`/subscription-plans/${renewTarget.id}/activate`, { start_date: renewStart, end_date: renewEnd });
      toast.success("Plan renewed and activated");
      setRenewTarget(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div data-testid="plans-list-page" className="mx-auto max-w-7xl">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Module 04 · Subscription Management</div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">Subscription Plans</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">Pick a periodical to view/manage its plans. Expired plans show as Inactive automatically.</p>
        </div>
        <Button asChild data-testid="plan-create-btn" disabled={!perId} className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] disabled:opacity-50">
          <Link to={perId ? `/subscription-plans/new?periodical_id=${perId}` : "#"}><Plus size={16} className="mr-1.5" />Create Plan</Link>
        </Button>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-3 rounded-sm border border-slate-200 bg-white p-4 sm:grid-cols-3">
        <div>
          <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Organization</Label>
          <Select value={orgId} onValueChange={setOrgId}>
            <SelectTrigger data-testid="plan-org-select" className="rounded-sm"><SelectValue placeholder="Select organization" /></SelectTrigger>
            <SelectContent>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Publication House</Label>
          <Select value={pubId} onValueChange={setPubId} disabled={!orgId}>
            <SelectTrigger data-testid="plan-pub-select" className="rounded-sm"><SelectValue placeholder={orgId ? "Select pub house" : "Pick org first"} /></SelectTrigger>
            <SelectContent>{pubs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Periodical</Label>
          <Select value={perId} onValueChange={setPerId} disabled={!pubId}>
            <SelectTrigger data-testid="plan-per-select" className="rounded-sm"><SelectValue placeholder={pubId ? "Select periodical" : "Pick pub house first"} /></SelectTrigger>
            <SelectContent>{pers.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {!perId ? (
        <div className="rounded-sm border border-dashed border-slate-300 bg-white p-12 text-center text-sm text-slate-500">
          <CalendarClock className="mx-auto mb-3 text-slate-400" size={28} />
          Select a periodical above to view its subscription plans.
        </div>
      ) : (
        <div className="rounded-sm border border-slate-200 bg-white">
          <Table data-testid="plan-table" className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Plan Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Validity</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items === null && [...Array(3)].map((_, i) => (
                <TableRow key={i}><TableCell colSpan={7}><Skeleton className="h-6 w-full" /></TableCell></TableRow>
              ))}
              {items && items.length === 0 && (
                <TableRow><TableCell colSpan={7} className="py-12 text-center text-sm text-slate-500">No plans yet for this periodical.</TableCell></TableRow>
              )}
              {items?.map((it) => {
                const final = (it.price || 0) * (1 + (it.tax_percentage || 0) / 100);
                return (
                <TableRow key={it.id} data-testid={`plan-row-${it.id}`}>
                  <TableCell className="font-medium text-slate-900">{it.plan_name}</TableCell>
                  <TableCell>
                    <span className={`rounded-sm border px-2 py-0.5 text-xs font-medium ${it.type === "Offer Plan" ? "border-rose-200 bg-rose-50 text-rose-700" : "border-indigo-200 bg-indigo-50 text-[#002FA7]"}`}>{it.type}</span>
                  </TableCell>
                  <TableCell className="text-sm text-slate-700 font-mono">{ymFromMonths(it.total_months)}</TableCell>
                  <TableCell className="text-sm text-slate-700">{fmtDate(it.start_date)} → {fmtDate(it.end_date)}</TableCell>
                  <TableCell className="text-sm text-slate-900 font-mono">{formatMoney(final, it.currency)}</TableCell>
                  <TableCell><StatusBadge status={it.effective_status || it.status} /></TableCell>
                  <TableCell className="text-right whitespace-nowrap" style={{ minWidth: 200 }}>
                    <div className="flex items-center justify-end gap-1">
                      <Button size="icon" variant="ghost" title="View" onClick={() => setView(it)} data-testid={`plan-view-${it.id}`} className="h-8 w-8 rounded-sm text-slate-600 hover:bg-slate-100"><Eye size={16} /></Button>
                      <Button size="icon" variant="ghost" title="Edit" onClick={() => navigate(`/subscription-plans/${it.id}/edit`)} data-testid={`plan-edit-${it.id}`} className="h-8 w-8 rounded-sm text-[#002FA7] hover:bg-indigo-50"><Pencil size={16} /></Button>
                      {(it.effective_status === "inactive" || it.status === "disabled") ? (
                        <Button size="icon" variant="ghost" title="Activate / Renew" onClick={() => handleActivate(it)} data-testid={`plan-activate-${it.id}`} className="h-8 w-8 rounded-sm text-emerald-600 hover:bg-emerald-50"><RotateCcw size={16} /></Button>
                      ) : (
                        <Button size="icon" variant="ghost" title="Disable" onClick={() => setDisableTarget(it)} data-testid={`plan-disable-${it.id}`} className="h-8 w-8 rounded-sm text-rose-600 hover:bg-rose-50"><Power size={16} /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );})}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent data-testid="plan-view-modal" className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle>Plan Preview</DialogTitle>
            <DialogDescription>How customers will see this plan.</DialogDescription>
          </DialogHeader>
          {view && <PriceCard plan={view} />}
        </DialogContent>
      </Dialog>

      <DisableConfirm
        open={!!disableTarget}
        onOpenChange={(o) => !o && setDisableTarget(null)}
        onConfirm={handleDisable}
        loading={disabling}
        entityLabel="subscription plan"
        warningMessage="Disabling this plan stops new subscriptions on it. Existing customers continue until their end_date. Soft-deleted; preserved for compliance."
      />

      <Dialog open={!!renewTarget} onOpenChange={(o) => !o && setRenewTarget(null)}>
        <DialogContent data-testid="plan-renew-modal" className="max-w-md rounded-sm border-amber-200">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-amber-50 text-amber-700"><AlertTriangle size={20} /></div>
              <div>
                <DialogTitle>Renew & Activate</DialogTitle>
                <DialogDescription>This plan expired on {fmtDate(renewTarget?.end_date)}. Set new validity dates to reactivate.</DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">New Start Date <span className="text-rose-600">*</span></Label>
              <Input type="date" value={renewStart} onChange={(e) => setRenewStart(e.target.value)} min={new Date().toISOString().slice(0, 10)} data-testid="renew-start-input" className="rounded-sm" />
            </div>
            <div>
              <Label className="text-sm">New End Date <span className="text-rose-600">*</span></Label>
              <Input type="date" value={renewEnd} onChange={(e) => setRenewEnd(e.target.value)} min={renewStart || new Date().toISOString().slice(0, 10)} data-testid="renew-end-input" className="rounded-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenewTarget(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={submitRenew} disabled={!renewStart || !renewEnd} data-testid="renew-submit-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#00227A]">Renew & Activate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PriceCard({ plan }) {
  const base = plan.price || 0;
  const tax = (base * (plan.tax_percentage || 0)) / 100;
  const final = base + tax;
  const cur = plan.currency || "INR";
  return (
    <div data-testid="price-card" className="rounded-sm border border-slate-200 bg-gradient-to-br from-indigo-50 via-white to-white p-5">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#002FA7]">{plan.type}</div>
        <span className="rounded-sm border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-mono font-semibold text-slate-700">{cur}</span>
      </div>
      <div className="mt-1 text-xl font-semibold tracking-tight text-slate-900">{plan.plan_name}</div>
      <div className="mt-3 flex items-baseline gap-1">
        <span className="text-3xl font-semibold text-slate-900">{formatMoney(final, cur)}</span>
        <span className="text-xs text-slate-500">/ {ymFromMonths(plan.total_months)}</span>
      </div>
      <div className="mt-3 space-y-1 border-t border-slate-200 pt-3 text-xs text-slate-600">
        <div className="flex justify-between"><span>Base price</span><span className="font-mono">{formatMoney(base, cur)}</span></div>
        <div className="flex justify-between"><span>GST ({plan.tax_percentage || 0}%)</span><span className="font-mono">{formatMoney(tax, cur)}</span></div>
        <div className="flex justify-between font-semibold text-slate-900"><span>Final</span><span className="font-mono">{formatMoney(final, cur)}</span></div>
      </div>
      <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
        <span>Valid: {fmtDate(plan.start_date)} → {fmtDate(plan.end_date)}</span>
        {plan.grace_period_days ? <span className="rounded-sm bg-slate-100 px-1.5 py-0.5">+{plan.grace_period_days}d grace</span> : null}
      </div>
    </div>
  );
}
